import React, { useEffect, useState, useCallback, memo, useMemo, useRef } from "react";
import {
	Tooltip,
	OverlayTrigger,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { EmployeeLink } from "components/Employees/EmployeeEditDrawer";
import { FaMapMarkerAlt, FaRegComment, FaKeyboard, FaGlobe } from "react-icons/fa";
import { FiAlertTriangle } from "react-icons/fi";
import { GoAlert } from "react-icons/go";
import { FaMobileAlt, FaDesktop } from "react-icons/fa";
import { BsCreditCard2Front } from "react-icons/bs";
import { GrRadialSelected } from "react-icons/gr";
import moment from "moment";
import { formatLocalizedDate } from "utils/dateConfig";
import Switch from "components/Switch";
import { useAttendance } from "../../hooks/attendance/AttendanceContext";
import "./Table.scss";
import { TIME_ALERTS } from "components/Times/utils";
import { convertFromUTC } from "utils/datetime";
import { timesService } from "services/timesService";
import { createApprovalLock } from "utils/approvalInFlight";
import { shiftType } from "services/shiftsService";
import { hideModal, showModal } from "actions/modalActions";
import { useDispatch, useSelector } from "react-redux";
import TimeObservationEdit from "components/TimeRegistrations/TimeObservationEditModal";
import TimeRegistrationLogModal from "components/TimeRegistrations/TimeRegistrationLogModal";
import { ReactComponent as AlertIcon } from "assets/icons/alert.svg";
import { RiEyeLine } from "react-icons/ri";
import DataTable from "components/DataTable";
import { trackHaddockEvent, getApprovalActorProps } from "utils/posthogTracking";
import { formatPlannedShift, getPlannedShiftIntervals } from "./utils/tableUtils";
import { hasTimeChatOrAttachments, isTempTimeRegistration } from "utils/timeChatUtils";
import { useTranslation } from "react-i18next";

const TIME_LIST_VIEWS = {
	list: 1,
	employee_day: 2,
	employee_week: 3,
};

// ---------------------------------------------------------------------------
// Helpers puros
// ---------------------------------------------------------------------------

const getDeviceType = (origin) => {
	if (!origin) return null;
	return origin.includes("APP") ? "mobile" : "desktop";
};

const formatWorkedTime = (start, end) => {
	if (!start || !end) return "0h 00'";
	const duration = moment.duration(moment(end).diff(moment(start)));
	const hours = Math.floor(duration.asHours());
	const minutes = Math.floor(duration.asMinutes() % 60);
	return `${hours}h ${minutes.toString().padStart(2, "0")}'`;
};

const renderDeviceIcon = (itemId, type, deviceOrigin, item = null, t = (k) => k) => {
	if (!deviceOrigin) return null;

	const deviceType = getDeviceType(deviceOrigin);
	let icon = null;
	let tooltipText = deviceOrigin;

	if (deviceOrigin.toLowerCase() === "terminal") {
		icon = (
			<BsCreditCard2Front
				className="device-icon terminal"
				style={{ color: "#6c757d", cursor: "pointer" }}
			/>
		);
		const terminalCode = item?.terminal_code2 || "desconocido";
		tooltipText = (
			<div>
				<div>{deviceOrigin}</div>
				<div>{t('attendance.terminalCode', { code: terminalCode })}</div>
			</div>
		);
	} else if (deviceType === "mobile") {
		icon = (
			<FaMobileAlt
				className="device-icon mobile"
				style={{ color: "#6c757d", cursor: "pointer" }}
			/>
		);
	} else {
		icon = (
			<FaDesktop
				className="device-icon desktop"
				style={{ color: "#6c757d", cursor: "pointer" }}
			/>
		);
	}

	return (
		<OverlayTrigger
			trigger="click"
			rootClose
			placement="top"
			overlay={(props) => (
				<Tooltip id={`tooltip-device-${itemId}-${type}`} {...props}>
					{tooltipText}
				</Tooltip>
			)}
		>
			<span style={{ marginLeft: "6px", display: "inline-block" }}>
				{icon}
			</span>
		</OverlayTrigger>
	);
};

const renderLocationIcon = (itemId, type, ip, lat, lon, t = (k) => k) => {
	if (!lat || !lon) return null;

	return (
		<OverlayTrigger
			trigger="click"
			rootClose
			placement="top"
			overlay={(props) => (
				<Tooltip
					id={`tooltip-location-${itemId}-${type}`}
					{...props}
					className="location-tooltip"
				>
					<div className="popover-content">
						<ul>
							<li><strong>IP:</strong> {ip || "N/A"}</li>
							<li><strong>LAT:</strong> {lat || "N/A"}</li>
							<li><strong>LNG:</strong> {lon || "N/A"}</li>
						</ul>
					</div>
					<button
						type="button"
						className="btn btn-link link-pink"
						onClick={() =>
							window.open(
								`https://www.google.com/maps?q=${lat},${lon}`,
								"_blank"
							)
						}
						style={{ padding: "0", margin: "2px 0 0 0", fontSize: "0.8rem" }}
					>
						{t('attendance.viewInMaps')}
					</button>
				</Tooltip>
			)}
		>
			<span style={{ marginLeft: "6px", display: "inline-block" }}>
				<FaMapMarkerAlt
					className="location-icon"
					style={{ color: "#6c757d", cursor: "pointer" }}
				/>
			</span>
		</OverlayTrigger>
	);
};

const renderSourceIcon = (itemId, type, source, t = (k) => k, deviceOrigin = null) => {
	if (!source) return null;

	let icon = null;
	let tooltipText = "";

	switch (source) {
		case "HOMEPAGE_WIDGET":
			icon = <GrRadialSelected className="source-icon" />;
			tooltipText = t('times.device.clockButton');
			break;
		case "TIME_REGISTRATIONS":
			icon = <FaKeyboard className="source-icon" />;
			tooltipText = t('times.device.keyboard');
			break;
		case "TERMINAL":
			// renderDeviceIcon already shows the card icon when origin is terminal
			if (deviceOrigin && deviceOrigin.toLowerCase() === "terminal") {
				return null;
			}
			icon = <BsCreditCard2Front className="source-icon" />;
			tooltipText = t('times.device.terminal');
			break;
		case "WEB_TERMINAL":
			icon = <FaGlobe className="source-icon" />;
			tooltipText = t('times.device.webTerminal');
			break;
		case "MODIFIED":
			return null;
		default:
			return null;
	}

	return (
		<OverlayTrigger
			placement="top"
			overlay={<Tooltip id={`source-${itemId}-${type}`}>{tooltipText}</Tooltip>}
		>
			<span style={{ marginLeft: "6px", display: "inline-block" }}>
				{React.cloneElement(icon, {
					style: { color: "#6c757d", cursor: "pointer" },
				})}
			</span>
		</OverlayTrigger>
	);
};

const renderAlertIcon = (item, t = (k) => k) => {
	if (!item || !item.alerts || item.alerts.length === 0) return null;

	const iconStyle = {
		display: "inline-block",
		fontSize: "18px",
		color: item.alerts.some((alert) => alert.type === "warning")
			? "#f0ad4e"
			: "#d9534f",
		cursor: "pointer",
	};

	const getTooltipAlertContent = (alerts) => {
		return alerts.map((alert, i) => {
			const plan_time = convertFromUTC(alert.plan_time);
			return (
				<li key={i}>
					<GoAlert className="tooltip-icon icon-alert" />{" "}
					{t('times.alerts.' + alert.type) || alert.type || t('attendance.noAlertDesc')}
					{alert.plan_time && <>. {t('times.alerts.planned')}: {plan_time}</>}
				</li>
			);
		});
	};

	return (
		<OverlayTrigger
			placement="top"
			container={document.body}
			popperConfig={{
				modifiers: [
					{
						name: "preventOverflow",
						options: { boundary: document.body },
					},
				],
			}}
			overlay={
				<Tooltip
					id={`tooltip-alert-${item.id || Math.random().toString(36).substr(2, 9)}`}
					className="tooltip-alerta"
				>
					<ul>{getTooltipAlertContent(item.alerts)}</ul>
				</Tooltip>
			}
		>
			<span className="alert-wrapper" style={iconStyle}>
				{item.alerts.some((alert) => alert.type === "warning") ? (
					<FiAlertTriangle className="warning-icon" />
				) : (
					<GoAlert className="alert-icon" />
				)}
			</span>
		</OverlayTrigger>
	);
};

// ---------------------------------------------------------------------------
// Celdas memoizadas (misma referencia de item → sin re-render)
// ---------------------------------------------------------------------------

const SupervisorEmployeeCell = memo(function SupervisorEmployeeCell({ item }) {
	return (
		<div className="employee-info-wrapper">
			<div className="employee-avatar-wrapper">
				{item.employee?.name ? item.employee.name.charAt(0).toUpperCase() : ""}
				{item.employee?.surname ? item.employee.surname.charAt(0).toUpperCase() : ""}
			</div>
			<div className="employee-details">
				<div className="flex flex-row">
					<EmployeeLink
						employee={item.employee}
						to={`/times/${item.employee?.id}/employee`}
						className="employee-name-link"
					>
						{item.employee
							? `${item.employee.name} ${item.employee.surname}`
							: ""}
					</EmployeeLink>
					{item.employee?.code ? <span className="employee-id"> · ID #{item.employee.code}</span> : ""}
				</div>
				<div className="employee-meta">
					{(() => {
						const ubication = item.employee?.employee_with_ubication?.ubication;
						if (!ubication?.name) return null;
						return (
							<span
								className="employee-badge-location"
								style={{
									backgroundColor: ubication?.background_color || "transparent",
									border: "1px solid #dee2e6",
									color: ubication?.color || "#065f46",
								}}
							>
								{ubication?.name}
							</span>
						);
					})()}
					{(() => {
						const funct = item.employee?.employee_with_function?.function?.name || item.employee?.position || item.employee?.job_title?.name;
						if (!funct) return null;
						return (
							<span className="employee-role">
								{funct}
							</span>
						);
					})()}
				</div>
			</div>
		</div>
	);
}, (prev, next) => prev.item === next.item);

const SupervisorDayCell = memo(function SupervisorDayCell({ item }) {
	return <>{formatLocalizedDate(item.day, "weekdayLong")}</>;
}, (prev, next) => prev.item === next.item);

const SupervisorPlannedShiftCell = memo(function SupervisorPlannedShiftCell({
	assignments,
}) {
	const shiftIntervals = getPlannedShiftIntervals(assignments);
	if (!shiftIntervals?.length) return <span className="no-time">--:--</span>;
	return (
		<div className="planned-shift-list">
			{shiftIntervals.map((shiftInterval, index) => (
				<span key={`${shiftInterval}-${index}`} className="planned-shift-text">
					{shiftInterval}
				</span>
			))}
		</div>
	);
});

const renderModifiedIcon = (itemId, type, source, item, onOpenLogs, t = (k) => k) => {
	if (source !== "MODIFIED") return null;
	return (
		<OverlayTrigger
			placement="top"
			overlay={<Tooltip id={`modified-${itemId}-${type}`}>{t('attendance.modifiedHistory')}</Tooltip>}
		>
			<span
				role="button"
				tabIndex={0}
				style={{ marginLeft: "6px", display: "inline-flex", alignItems: "center", cursor: "pointer" }}
				onClick={() => onOpenLogs(item)}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						onOpenLogs(item);
					}
				}}
			>
				<AlertIcon style={{ width: "20px", height: "auto" }} />
			</span>
		</OverlayTrigger>
	);
};

const SupervisorStartCell = memo(function SupervisorStartCell({ item, onOpenLogs }) {
	const { t } = useTranslation();
	return item.start ? (
		<div className="time-cell-content">
			<span className="time">
				{moment(item.start).format("HH:mm")}
			</span>
			{renderDeviceIcon(item.id, 'in', item.start_origin, item, t)}
			{renderSourceIcon(item.id, 'in', item.start_source, t, item.start_origin)}
			{renderLocationIcon(
				item.id,
				"in",
				item.start_ip,
				item.start_lat,
				item.start_lon,
				t
			)}
			{renderModifiedIcon(item.id, "in", item.start_source, item, onOpenLogs, t)}
		</div>
	) : (
		<span className="no-time">--:--</span>
	);
}, (prev, next) => prev.item === next.item && prev.onOpenLogs === next.onOpenLogs);

const SupervisorEndCell = memo(function SupervisorEndCell({ item, onOpenLogs }) {
	const { t } = useTranslation();
	return item.end ? (
		<div className="time-cell-content">
			<span className="time">
				{moment(item.end).format("HH:mm")}
			</span>
			{renderDeviceIcon(item.id, 'out', item.end_origin, item, t)}
			{renderSourceIcon(item.id, 'out', item.end_source, t, item.end_origin)}
			{renderLocationIcon(
				item.id,
				"out",
				item.end_ip,
				item.end_lat,
				item.end_lon,
				t
			)}
			{renderModifiedIcon(item.id, "out", item.end_source, item, onOpenLogs, t)}
		</div>
	) : (
		<span className="no-time">--:--</span>
	);
}, (prev, next) => prev.item === next.item && prev.onOpenLogs === next.onOpenLogs);

const SupervisorHoursCell = memo(function SupervisorHoursCell({ item }) {
	return <>{formatWorkedTime(item.start, item.end)}</>;
}, (prev, next) => prev.item === next.item);

const SupervisorTypeCell = memo(function SupervisorTypeCell({ item }) {
	const { t } = useTranslation();
	const name = item.type?.name;
	return (
		<div className={`type-badge ${name === shiftType.work ? "trabajo" : "other"}`}>
			{name ? t(`timeTypes.names.${name}`, { defaultValue: name }) : "—"}
		</div>
	);
}, (prev, next) => prev.item === next.item);

const SupervisorAlertCell = memo(function SupervisorAlertCell({ item, onOpenObservations }) {
	const { t } = useTranslation();
	const renderObservationsIcon = () => {
		if (!hasTimeChatOrAttachments(item)) return null;
		return (
			<FaRegComment
				className="icon-comment"
				title={t('times.chat.viewConversation')}
				onClick={() => onOpenObservations(item, t('times.table.viewObservations'))}
			/>
		);
	};

	return (
		<>
			{renderObservationsIcon()}
			{renderAlertIcon(item, t)}
		</>
	);
}, (prev, next) => prev.item === next.item && prev.onOpenObservations === next.onOpenObservations);

const SupervisorApprovedCell = memo(function SupervisorApprovedCell({ item, onSwitchToggle, disabled }) {
	return (
		<Switch
			controlId={`approved-${item.id}`}
			isOn={item.approved}
			disabled={disabled}
			handleToggle={(event) => onSwitchToggle(event, item)}
		/>
	);
}, (prev, next) =>
	prev.item === next.item &&
	prev.onSwitchToggle === next.onSwitchToggle &&
	prev.disabled === next.disabled
);

function buildSupervisorColumns(sortableFields, handlers, plannedShifts, t = (k) => k) {
	const isSort = (f) => sortableFields[f] === true;
	const { handleSwitchToggle, openObservationsModal, openLogsModal, isApprovingId } = handlers;

	return [
		{
			key: "employee",
			header: t('common.employee'),
			className: "employee-cell",
			headerClassName: `employee-cell${isSort("employee") ? " sortable" : ""}`,
			sortable: isSort("employee"),
			render: (item) => <SupervisorEmployeeCell item={item} />,
		},
		{
			key: "day",
			header: t('common.day'),
			className: "day-cell",
			headerClassName: `day-cell${isSort("day") ? " sortable" : ""}`,
			sortable: isSort("day"),
			render: (item) => <SupervisorDayCell item={item} />,
		},
		{
			key: "planned_shift",
			header: t('common.shift'),
			className: "planned-shift-cell",
			headerClassName: "planned-shift-cell",
			sortable: false,
			render: (item) => {
				const empId = item.employee?.id || item.employee_id;
				const dayKey = item.day
					? moment(item.day).format("YYYY-MM-DD")
					: item.start
					? moment(item.start).format("YYYY-MM-DD")
					: null;
				if (!empId || !dayKey) {
					return <span className="no-time">--:--</span>;
				}
				const assignments = plannedShifts?.[`${empId}_${dayKey}`];
				return <SupervisorPlannedShiftCell assignments={assignments} />;
			},
		},
		{
			key: "start",
			header: t('attendance.entryStamping'),
			className: "time-cell",
			headerClassName: "time-cell",
			sortable: false,
			render: (item) => <SupervisorStartCell item={item} onOpenLogs={openLogsModal} />,
		},
		{
			key: "end",
			header: t('attendance.exitStamping'),
			className: "time-cell",
			headerClassName: "time-cell",
			sortable: false,
			render: (item) => <SupervisorEndCell item={item} onOpenLogs={openLogsModal} />,
		},
		{
			key: "worked_time",
			header: t('times.table.hours'),
			className: "hours-cell",
			headerClassName: "hours-cell",
			sortable: false,
			render: (item) => <SupervisorHoursCell item={item} />,
		},
		{
			key: "type",
			header: t('common.type'),
			className: "type-cell",
			headerClassName: `type-cell${isSort("type") ? " sortable" : ""}`,
			sortable: isSort("type"),
			render: (item) => <SupervisorTypeCell item={item} />,
		},
		{
			key: "alerts",
			header: "\u00A0",
			className: "alert-column",
			headerClassName: "alert-column",
			sortable: false,
			render: (item) => (
				<SupervisorAlertCell item={item} onOpenObservations={openObservationsModal} />
			),
		},
		{
			key: "approved",
			header: t('times.table.approved'),
			className: "approved-column",
			headerClassName: `approved-column${isSort("approved") ? " sortable" : ""}`,
			sortable: isSort("approved"),
			align: "center",
			render: (item) => (
				<SupervisorApprovedCell
					item={item}
					onSwitchToggle={handleSwitchToggle}
					disabled={isApprovingId?.(item.id)}
				/>
			),
		},
	];
}

// ---------------------------------------------------------------------------
// Tabla principal
// ---------------------------------------------------------------------------

const Table = ({ timeListView = TIME_LIST_VIEWS.list }) => {
	const { t } = useTranslation();
	const {
		items,
		loading,
		loadingMore,
		hasMore,
		fetchMoreData,
		applySort,
		sortConfig,
		setItems,
		plannedShifts,
	} = useAttendance();

	const [hasScroll, setHasScroll] = useState(false);
	const dispatch = useDispatch();
	const user = useSelector((state) => state.authReducer.user);
	const approvalLockRef = useRef(createApprovalLock());
	const [approvingIds, setApprovingIds] = useState(() => new Set());

	const openLogsModal = useCallback((item) => {
		dispatch(
			showModal({
				modalProps: {
					open: true,
					title: t('times.logModal.title'),
					style: { width: "900px" },
					content: (
						<TimeRegistrationLogModal
							time={item}
							handleClose={() => dispatch(hideModal())}
						/>
					),
					closeModal: () => dispatch(hideModal()),
				},
				modalType: "edit",
			})
		);
	}, [dispatch]);

	const openObservationsModal = useCallback((time, titleObservations) => {
		if (isTempTimeRegistration(time)) {
			dispatch(
				showModal({
					modalProps: {
						open: true,
						style: { width: "400px" },
						title: t("times.registrations.addErrorTitle"),
						message: t("times.chat.saveTimeBeforeMessage"),
						closeModal: () => dispatch(hideModal()),
					},
					modalType: "alert",
				})
			);
			return;
		}

		dispatch(
			showModal({
				modalProps: {
					open: true,
					title: titleObservations,
					style: { width: "520px", maxWidth: "95vw" },
					content: (
						<TimeObservationEdit
							time={time}
							employee={time.employee}
							handleClose={() => dispatch(hideModal())}
						/>
					),
					closeModal: () => dispatch(hideModal()),
				},
				modalType: "edit",
			})
		);
	}, [dispatch, t]);

	const handleSwitchToggle = useCallback((event, time) => {
		const isApproved = event.target.checked;
		const key = String(time.id);
		if (!approvalLockRef.current.tryBegin(key)) {
			return;
		}
		setApprovingIds((prev) => {
			const next = new Set(prev);
			next.add(key);
			return next;
		});

		trackHaddockEvent(
			"time_approval__single_record__toggled__attendance_supervisor",
			{
				...getApprovalActorProps(user),
				surface: "attendance_supervisor",
				action_type: "single_toggle",
				toggle_direction: isApproved ? "approve" : "unapprove",
				new_approved: isApproved,
				target_time_registration_id: time.id,
				target_employee_id: time.employee?.id || time.employee_id || null,
				target_day: moment(time.day || time.start).format("YYYY-MM-DD"),
				affected_records_count: 1,
				time_list_view: timeListView,
			}
		);

		if (!time.start || !time.end) {
			approvalLockRef.current.end(key);
			setApprovingIds((prev) => {
				const next = new Set(prev);
				next.delete(key);
				return next;
			});
			dispatch(
				showModal({
					modalProps: {
						open: true,
						style: { width: "400px" },
						title: "Error",
						message:
							t('attendance.approveNeedsStartEnd'),
						closeModal: () => dispatch(hideModal()),
					},
					modalType: "alert",
				})
			);
			return;
		}

		setItems((prev) =>
			prev.map((item) =>
				item.id === time.id ? { ...item, approved: isApproved } : item
			)
		);

		timesService.approve([{ id: time.id, approved: isApproved }]).catch((error) => {
			console.error(
				`Error al ${isApproved ? "aprobar" : "desaprobar"} los registros:`,
				error
			);

			let errorMessage = `Error al ${isApproved ? "aprobar" : "desaprobar"} los registros.`;
			if (error.response?.data?.errors?.length > 0) {
				errorMessage = error.response.data.errors[0].description || errorMessage;
			} else if (error.message) {
				errorMessage = error.message;
			}

			dispatch(
				showModal({
					modalProps: {
						open: true,
						style: { width: "400px" },
						title: "Error",
						message: errorMessage,
						closeModal: () => dispatch(hideModal()),
					},
					modalType: "alert",
				})
			);

			setItems((prev) =>
				prev.map((item) =>
					item.id === time.id ? { ...item, approved: !isApproved } : item
				)
			);
		}).finally(() => {
			approvalLockRef.current.end(key);
			setApprovingIds((prev) => {
				const next = new Set(prev);
				next.delete(key);
				return next;
			});
		});
	}, [dispatch, setItems, user, timeListView, t]);

	const sortableFields = useMemo(() => {
		if (timeListView === TIME_LIST_VIEWS.list) {
			return { employee: true, day: true, type: true, approved: true };
		}
		return {};
	}, [timeListView]);

	const isApprovingId = useCallback(
		(id) => approvingIds.has(String(id)),
		[approvingIds]
	);

	const columns = useMemo(
		() =>
			buildSupervisorColumns(
				sortableFields,
				{
					handleSwitchToggle,
					openObservationsModal,
					openLogsModal,
					isApprovingId,
				},
				plannedShifts,
				t
			),
		[
			sortableFields,
			handleSwitchToggle,
			openObservationsModal,
			openLogsModal,
			isApprovingId,
			plannedShifts,
			t,
		]
	);

	useEffect(() => {
		const timer = setTimeout(() => {
			const el = document.getElementById("infinite-table-scrollable-target");
			if (el) {
				setHasScroll(el.scrollHeight > el.clientHeight);
			}
		}, 100);
		return () => clearTimeout(timer);
	}, [items]);

	if (timeListView === TIME_LIST_VIEWS.employee_day) {
		return null;
	}

	if (loading && items.length === 0) {
		return (
			<div className="loader-container full-page">
				<div className="loader"></div>
				<div className="loading-text">{t('attendance.loadingData')}</div>
			</div>
		);
	}

	const sortState =
		sortConfig && sortConfig.field
			? { key: sortConfig.field, direction: sortConfig.direction }
			: null;

	return (
		<>
			<DataTable
				className="attendance-supervisor-datatable-scroll"
				tableClassName="times-table table-hover"
				columns={columns}
				rows={items || []}
				rowKey={(row) => row.id}
				emptyMessage={
					loadingMore ? t('common.loading') : t('attendance.noRecords')
				}
				sortState={sortState}
				onSortChange={({ key, direction }) => {
					if (sortableFields[key]) {
						applySort(key, direction);
					}
				}}
				actionsColumn={{
					header: t('attendance.viewStampings'),
					headerClassName: "view-stamping-column",
					render: (item) => (
						<Link
							to={`/times/${item.employee?.id}/employee`}
							className="view-stamping-link"
							title={t('attendance.viewStampings')}
						>
							<RiEyeLine />
						</Link>
					),
					className: "view-stamping-column",
				}}
				infiniteScroll={{
					dataLength: items?.length || 0,
					next: fetchMoreData,
					hasMore,
					loader:
						hasScroll && loadingMore ? (
							<div className="text-center py-2">{t('common.loading')}</div>
						) : null,
					endMessage:
						items && items.length > 0 ? (
							<p className="no-more-results text-center">
								{t('times.noMoreRecords')}
							</p>
						) : null,
					scrollThreshold: 0.9,
					scrollableTarget: "infinite-table-scrollable-target",
				}}
				scrollContainerId="infinite-table-scrollable-target"
				scrollContainerClassName="table-responsive attendance-supervisor-scroll-wrap"
			/>
		</>
	);
};

export default Table;
